import { CategoryName, DisplayModeName } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { arrayExt } from '../../framework/language/ArrayExt';
import UniqueId from '../../services/uid/UniqueId';

import { emptyAction } from './actionHandler';

export const enum ContentEntityType {
  CATEGORY = 'category',
  BALANCE_FORM = 'balanceForm',
}

interface ContentEntity {
  type: ContentEntityType;
  content: UniqueId;
  title: string;
  pinned?: boolean;
}

export interface OpenContentEntity extends ContentEntity {
  id: UniqueId;
}

export interface ContentState {
  readonly entities: Record<UniqueId, OpenContentEntity>;
  readonly order: UniqueId[];
  readonly active: UniqueId | null;
  readonly lastUsed: UniqueId[];
}

export const MAX_PINNED_TABS = 5;

const initialState: ContentState = {
  entities: {},
  order: [],
  active: null,
  lastUsed: [],
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    addEntity: emptyAction<ContentEntity>(),
    open(
      state,
      {
        payload: { id, contentEntity },
      }: PayloadAction<{ id: UniqueId; contentEntity: ContentEntity }>
    ) {
      const entity = { ...contentEntity, id };

      state.entities[entity.id] = entity;
      state.order.push(entity.id);
      state.active = entity.id;
      state.lastUsed.unshift(entity.id);
    },
    close: emptyAction<{ id: UniqueId; save?: boolean }>(),
    closeSuccess(state, { payload: { id } }: PayloadAction<{ id: UniqueId }>) {
      delete state.entities[id];
      arrayExt(state.order).remove(id);
      state.active = null;
      arrayExt(state.lastUsed).remove(id);

      if (state.lastUsed.length > 0) {
        state.active = state.lastUsed[0];
      }
    },
    activate(state, { payload: { id } }: PayloadAction<{ id: UniqueId }>) {
      state.active = id;
      arrayExt(state.lastUsed).remove(id);
      state.lastUsed.unshift(id);
    },
    activateDisplayMode: emptyAction<{
      category: CategoryName;
      displayMode: DisplayModeName;
    }>(),
    togglePin(state, { payload: { id } }: PayloadAction<{ id: UniqueId }>) {
      const entity = state.entities[id];
      if (
        !entity ||
        (!entity.pinned &&
          Object.values(state.entities).filter(({ pinned }) => pinned).length >=
            MAX_PINNED_TABS)
      )
        return;

      entity.pinned = !entity.pinned;
    },
    reorder(
      state,
      {
        payload: { fromIndex, toIndex },
      }: PayloadAction<{ fromIndex: number; toIndex: number }>
    ) {
      const [removed] = state.order.splice(fromIndex, 1);
      state.order.splice(toIndex, 0, removed);
    },
  },
});

export const contentReducer = contentSlice.reducer;

export default contentSlice.actions;
